/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

// eslint-disable-next-line import/no-restricted-paths
import { queryDeploymentProxyForConsole } from '../../../public/actions/apiConsole'
import { TEMPLATE_REQUEST } from '../autocomplete_entities'

import {
  createJsonResponse,
  makeHttpRequest,
  normalizeDeploymentProxyResult,
  handleProxyError,
} from './utils'

import type { StackDeployment, HttpResponse, RouteMapper } from './types'

// Proxies requests to the deployment's Elasticsearch cluster
const handleProxyPost = async (deployment: StackDeployment, body: any): Promise<HttpResponse> => {
  try {
    const result = await queryDeploymentProxyForConsole(
      deployment!,
      {
        ...TEMPLATE_REQUEST,
        method: body.query.method,
        path: body.query.path,
        ...(body && body.body ? { body: body.body } : {}),
      },
      false,
    )

    const bodyresp = normalizeDeploymentProxyResult(result)
    const response = createJsonResponse(bodyresp)

    return {
      body: bodyresp,
      response,
    }
  } catch (error: any) {
    // If the request fails, return with the appropriate status code from the error
    // The API response already contains the status code in the body
    const errorBody = handleProxyError(error)
    const status = errorBody.status || 500
    const response = createJsonResponse(errorBody, status)

    return {
      body: errorBody,
      response,
    }
  }
}

const handleDefaultPost = async (
  path: string,
  body: any,
  mapRoute: RouteMapper,
  options?: any,
): Promise<any> => {
  const mappedPath = mapRoute(path)
  const response = await makeHttpRequest(mappedPath, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options,
  })
  const { body: bodyresp } = response

  return bodyresp
}

export const handlePostRequest = async (
  path: string,
  body: any,
  deployment: StackDeployment,
  mapRoute: RouteMapper,
  options?: any,
): Promise<any> => {
  if (path === '/api/console/proxy') {
    return handleProxyPost(deployment, body)
  }

  return handleDefaultPost(path, body, mapRoute, options)
}
